<template>
    <div>
        <input class="multi-entity-input form-control" ref="inputElement" type="text" />
    </div>
</template>

<script setup lang="ts">
    import { MultiEntityInput } from "@/components/formControls/MultiEntityInput";
    import type { IMultiEntityLinkDataSource } from "@/data/dao/service/BaseServiceDao";
    import { onMounted, ref, watch } from "vue";

    const selectedValues = defineModel<string[]>({
        required: true,
    });

    const props = defineProps<{
        dataSource: IMultiEntityLinkDataSource;
        name?: string;
    }>();

    const inputElement = ref<HTMLInputElement | null>(null);

    let numberInput: MultiEntityInput | undefined;
    onMounted(async () => {
        if (inputElement.value) {
            numberInput = MultiEntityInput.createFromInput($(inputElement.value), {
                dataSource: props.dataSource,
            });

            numberInput.setDisabled(true);
            const selectedEntities = await getSelectedEntities();
            numberInput.setDisabled(false);

            numberInput!.setValue(selectedEntities);
            numberInput!.onChange = (value) => {
                if (hasSelectionChanged(value, selectedValues.value)) {
                    selectedValues.value = [...value]; // need to create a new array to trigger reactivity
                }
            };
        }
    });

    watch(selectedValues, async (value) => {
        if (!numberInput) return;

        // need to check if the values are different to avoid infinite loop
        if (hasSelectionChanged(numberInput.getValue(), value)) {
            const selectedEntities = await getSelectedEntities();
            numberInput.setValue(selectedEntities);
        }
    });

    function setError(hasError: boolean) {
        numberInput?.setError(hasError);
    }

    async function getSelectedEntities() {
        const dataSourceData = await props.dataSource.search([]);
        return dataSourceData.filter((x) => selectedValues.value.includes(x.id));
    }

    function hasSelectionChanged(inputValues: string[], modelValues: string[]) {
        const numberInputValuesStr = inputValues.join(",");
        const selectedValuesStr = modelValues.join(",");
        return numberInputValuesStr !== selectedValuesStr;
    }

    async function refreshDataSource() {
        const selectedEntities = await getSelectedEntities();
        numberInput!.setValue(selectedEntities);
    }

    defineExpose({
        setError,
        getSelectedEntities,
        refreshDataSource,
    });
</script>
<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";

    .multi-entity-input {
        position: relative;
        background-color: #fff;
        background-clip: padding-box;
        border: 1px solid #e2e5ec;
        border-radius: 4px;
        transition: border-color 0.15s ease-in-out;
        font-size: 0;
        padding: 3px 5px;

        &.is-invalid {
            border-color: #fd397a;

            > .search {
                border-color: #fd397a;
            }
        }

        &.open {
            border-color: #9aabff;

            > .search {
                border-color: #9aabff;
            }
        }

        > input {
            border: none;
            width: auto;
            min-width: 80px;
            display: inline-block;
            vertical-align: top;
        }

        > .link {
            position: relative;
            display: inline-block;
            vertical-align: top;
            border-radius: 4px;
            padding: 0 28px 0 10px;
            line-height: 30px;
            font-size: 13px;
            letter-spacing: 0.2px;
            height: 32px;
            box-sizing: border-box;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            border: 1px solid colors.$green;
            color: #626262;
            margin: 3px;
            max-width: 100%;
            > .remover {
                position: absolute;
                top: 0;
                right: 0;
                height: 32px;
                padding: 0 10px;
                cursor: pointer;

                > .flaticon2-cross {
                    font-size: 10px;
                    color: #bababa;
                }

                &:hover {
                    > .flaticon2-cross {
                        color: #626262;
                    }
                }
            }

            > .appearsin {
                padding-left: 4px;
                font-size: 10px;
            }
        }

        > .search {
            position: absolute;
            z-index: 300;
            top: 35px;
            left: -1px;
            right: -1px;
            background-color: white;
            border: 1px solid #e2e5ec;
            border-top: 0;
            -webkit-border-bottom-right-radius: 4px;
            -webkit-border-bottom-left-radius: 4px;
            -moz-border-radius-bottomright: 4px;
            -moz-border-radius-bottomleft: 4px;
            border-bottom-right-radius: 4px;
            border-bottom-left-radius: 4px;
            display: none;
            min-height: 3px;
            transition: border-color 0.15s ease-in-out;
            max-height: 200px;
            overflow-y: auto;

            > .loading {
                padding: 10px 20px;
                font-size: 13px;

                > .kt-spinner {
                    display: inline-block;
                    vertical-align: middle;
                    margin: -2px 20px 0 0;
                }
            }

            > .nodata {
                padding: 10px 20px;
                font-size: 13px;
            }

            > .option {
                display: block;
                cursor: pointer;
                line-height: 34px;
                height: 34px;
                font-size: 13px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                padding: 0 1rem;
                box-sizing: border-box;

                &:hover {
                    background-color: #fbfbfb;
                }

                > .appearsin {
                    padding-left: 4px;
                    font-size: 10px;
                    color: #7f849a;
                }
            }
        }
    }
</style>
